import { useState, useEffect } from 'react';
import { useCustomMutation, useApiUrl } from '@refinedev/core';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  FileDown,
  CheckCircle2,
  Circle,
  StickyNote,
  ClipboardList,
  Paperclip,
  Star,
} from 'lucide-react';
import {
  type HomeworkRecord,
  getUserDisplayName,
  formatFileSize,
  HOMEWORK_STATUS_LABELS,
  HOMEWORK_STATUS_VARIANTS,
} from './types';

// ─── Types ──────────────────────────────────────────────────

interface HomeworkReviewDialogProps {
  homework: HomeworkRecord | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReviewed?: () => void;
}

// ─── Component ──────────────────────────────────────────────

export function HomeworkReviewDialog({
  homework,
  open,
  onOpenChange,
  onReviewed,
}: HomeworkReviewDialogProps) {
  const apiUrl = useApiUrl();
  const [grade, setGrade] = useState('');
  const [feedback, setFeedback] = useState('');

  const { mutate: reviewMutate, mutation: reviewMutation } = useCustomMutation();
  const isSubmitting = reviewMutation.isPending;

  // Сбросить состояние при открытии/смене ДЗ
  useEffect(() => {
    if (homework && open) {
      setGrade(homework.grade !== null ? String(homework.grade) : '');
      setFeedback(homework.feedback ?? '');
    }
  }, [homework, open]);

  const handleSubmit = () => {
    if (!homework) { return; }

    const gradeValue = grade !== '' ? Number(grade) : undefined;

    // Валидация
    if (gradeValue !== undefined && (gradeValue < 0 || gradeValue > 100 || Number.isNaN(gradeValue))) {
      return;
    }

    reviewMutate(
      {
        url: `${apiUrl}/homework/${homework.id}/review`,
        method: 'patch',
        values: {
          grade: gradeValue,
          feedback: feedback || undefined,
          status: 'REVIEWED' as const,
        },
      },
      {
        onSuccess: () => {
          onOpenChange(false);
          onReviewed?.();
        },
      },
    );
  };

  if (!homework) { return null; }

  const isAlreadyReviewed = homework.status === 'REVIEWED';
  const gradeNumber = grade !== '' ? Number(grade) : null;
  const isGradeValid = gradeNumber === null || (gradeNumber >= 0 && gradeNumber <= 100 && !Number.isNaN(gradeNumber));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Star className="h-5 w-5 text-amber-500" />
            Рецензия домашнего задания
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4">
          {/* Информация о ДЗ */}
          <div className="rounded-lg border p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{getUserDisplayName(homework.user)}</span>
              <Badge variant={HOMEWORK_STATUS_VARIANTS[homework.status]}>
                {HOMEWORK_STATUS_LABELS[homework.status]}
              </Badge>
            </div>
            <div className="text-xs text-muted-foreground space-y-0.5">
              <p>{homework.item.module.course.title} &rarr; {homework.item.module.title} &rarr; {homework.item.title}</p>
              {homework.submittedAt && (
                <p>Отправлено: {new Date(homework.submittedAt).toLocaleDateString('ru-RU', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}</p>
              )}
            </div>
          </div>

          {/* Файлы */}
          {homework.files && homework.files.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-1.5 text-sm font-medium">
                <Paperclip className="h-4 w-4" />
                Файлы ({homework.files.length})
              </div>
              <div className="space-y-1.5">
                {homework.files.map((file, idx) => (
                  <a
                    key={idx}
                    href={file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm hover:bg-accent transition-colors"
                  >
                    <FileDown className="h-4 w-4 text-blue-500 flex-shrink-0" />
                    <span className="flex-1 truncate">{file.name}</span>
                    <span className="text-xs text-muted-foreground flex-shrink-0">
                      {formatFileSize(file.size)}
                    </span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Чеклист */}
          {homework.checklistItems && homework.checklistItems.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-1.5 text-sm font-medium">
                <ClipboardList className="h-4 w-4" />
                Чеклист
              </div>
              <div className="space-y-1">
                {homework.checklistItems.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm">
                    {item.isCompleted ? (
                      <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                    ) : (
                      <Circle className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    )}
                    <span className={item.isCompleted ? 'line-through text-muted-foreground' : ''}>
                      {item.text}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Заметки */}
          {homework.notes && (
            <div className="space-y-2">
              <div className="flex items-center gap-1.5 text-sm font-medium">
                <StickyNote className="h-4 w-4" />
                Заметки
              </div>
              <div className="rounded-md border p-3 text-sm whitespace-pre-wrap bg-muted/50">
                {homework.notes}
              </div>
            </div>
          )}

          {/* Список заметок */}
          {homework.noteItems && homework.noteItems.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-1.5 text-sm font-medium">
                <StickyNote className="h-4 w-4" />
                Записи ({homework.noteItems.length})
              </div>
              <div className="space-y-1.5">
                {homework.noteItems.map((noteItem) => (
                  <div key={noteItem.id} className="rounded-md border p-2 text-sm">
                    <p>{noteItem.text}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(noteItem.createdAt).toLocaleDateString('ru-RU', {
                        day: '2-digit',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Форма рецензии */}
          <div className="space-y-3 border-t pt-4">
            <div className="space-y-1.5">
              <Label htmlFor="grade">Оценка (0–100)</Label>
              <Input
                id="grade"
                type="number"
                min={0}
                max={100}
                placeholder="Введите оценку..."
                value={grade}
                onChange={(e) => setGrade(e.target.value)}
                className={!isGradeValid ? 'border-destructive' : ''}
              />
              {!isGradeValid && (
                <p className="text-xs text-destructive">Оценка должна быть от 0 до 100</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="feedback">Обратная связь</Label>
              <Textarea
                id="feedback"
                placeholder="Напишите комментарий к работе..."
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                rows={4}
                maxLength={5000}
              />
              <p className="text-xs text-muted-foreground text-right">
                {feedback.length} / 5000
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Отмена
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !isGradeValid}
          >
            {isSubmitting ? 'Сохранение...' : isAlreadyReviewed ? 'Обновить рецензию' : 'Проверено'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
